import type { AiReportColumn, AiReportResult, ReportDateRange } from '@order-pilot/shared';
import { formatMoney, parseAmount } from '@/lib/format';

const PERIOD: Record<ReportDateRange, string> = {
  today: 'today',
  yesterday: 'yesterday',
  last_7_days: 'in the last 7 days',
  last_30_days: 'in the last 30 days',
  this_week: 'this week',
  this_month: 'this month',
  last_month: 'last month',
  this_year: 'this year',
};

/** An average of averages is not the average: these columns get no total. */
export const isSummable = (key: string) => !key.startsWith('average');

const toCents = (value: string): bigint => {
  const negative = value.trim().startsWith('-');
  const [whole, fraction = ''] = value.trim().replace('-', '').split('.');
  const cents = BigInt(whole || '0') * 100n + BigInt(`${fraction}00`.slice(0, 2));
  return negative ? -cents : cents;
};
const fromCents = (cents: bigint): string => {
  const abs = cents < 0n ? -cents : cents;
  return `${cents < 0n ? '-' : ''}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`;
};

/** Total of a column, exact for money (decimal strings, summed in cents). */
export function columnTotal(rows: AiReportResult['rows'], column: AiReportColumn): string | number {
  if (column.kind === 'money') return fromCents(rows.reduce((sum, row) => sum + toCents(String(row[column.key] ?? '0')), 0n));
  return rows.reduce<number>((sum, row) => sum + (Number(row[column.key]) || 0), 0);
}

export function formatValue(value: string | number | undefined, column: AiReportColumn, currency: string): string {
  if (column.kind === 'dimension') return String(value ?? '');
  if (column.kind === 'money') return formatMoney(String(value ?? '0'), currency);
  return (Number(value) || 0).toLocaleString();
}

/** Float, only for chart geometry and picking the largest row. */
export const numeric = (value: string | number | undefined) => (typeof value === 'number' ? value : parseAmount(value));

/**
 * The one-line answer above the chart, written from the figures themselves,
 * not by the AI: it can only say what the table says.
 * "Sales this month: AED 41,680.00. Main has the highest sales at AED 24,820.00."
 */
export function summarise(result: AiReportResult): string {
  const period = PERIOD[result.spec.range];
  const measure = result.columns.find((c) => c.kind !== 'dimension');
  const dim = result.columns.find((c) => c.kind === 'dimension');
  if (!measure) return '';
  if (result.rows.length === 0) return `Nothing recorded ${period}.`;
  let text = '';
  if (isSummable(measure.key)) {
    text = `${measure.label} ${period}: ${formatValue(columnTotal(result.rows, measure), measure, result.currency)}.`;
  } else if (!dim) {
    text = `${measure.label} ${period}: ${formatValue(result.rows[0][measure.key], measure, result.currency)}.`;
  }
  if (dim && result.rows.length > 1) {
    let best = 0;
    result.rows.forEach((row, i) => {
      if (numeric(row[measure.key]) > numeric(result.rows[best][measure.key])) best = i;
    });
    const top = result.rows[best];
    text += `${text ? ' ' : ''}${String(top[dim.key])} has the highest ${measure.label.toLowerCase()} at ${formatValue(top[measure.key], measure, result.currency)}.`;
  }
  return text;
}
